import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { AssignedProperty, Assignee, AssigneeType, PropertyClient } from 'api/clients/PropertyClient'
import { deleteFilesProperties, upsertFilesProperties } from 'redux/actions/filesProperties'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useQueryClient } from 'react-query'
import { articlesKeys } from './queries/articles/articlesKeys'
import { informalFeedbackRequestsKeys } from './queries/informalFeedbackRequests/informalFeedbackRequestsKeys'
import { RefreshPayload } from 'screens/Finder/FinderContextTypes'
import { almanacFileKeys } from './queries/almanacFiles/almanacFileKeys'
import { removeProperty } from 'redux/actions/properties'
import { useProperties } from './queries/companies/properties/useProperties'
import { Property } from 'api/clients/PropertyClient'

enum Operation {
  Invalidate = 'invalidate',
}

enum EntityType {
  Properties = 'properties',
  Article = 'Article',
  Project = 'Project',
  InformalFeedbackRequests = 'informal_feedback_requests',
  Attributes = 'attributes',
}

type InvalidatedProperties = {
  type: EntityType.Properties
  assignee: Assignee
  property_id: number
}

type InvalidatedAttributes = {
  type: EntityType.Attributes
}

type InvalidatedArticle = {
  type: EntityType.Article
  id: string
}

type InvalidatedProject = {
  type: EntityType.Project
  id: string
}

type InvalidatedInformalFeedbackRequests = {
  type: EntityType.InformalFeedbackRequests
}

type InvalidateEntity =
  | InvalidatedProperties
  | InvalidatedArticle
  | InvalidatedProject
  | InvalidatedInformalFeedbackRequests
  | InvalidatedAttributes

interface InvalidatePayload extends RefreshPayload {
  op: Operation.Invalidate
  entities: InvalidateEntity[]
  action?: string
}

export enum AssetType {
  Article = 'Article',
  Project = 'Project',
  Handbook = 'Handbook',
  MediaFile = 'MediaFile',
  Design = 'Design',
  Video = 'Video',
}

enum Action {
  addProperty = 'add_property',
  deleteProperty = 'delete_property',
}

interface Asset {
  id: string
  type: AssetType
}

interface Props {
  asset: Asset
  onAttributesChange?: (payload: RefreshPayload) => void
  onPropertyChange?: (propertyId: number, assignedProperty: AssignedProperty[]) => void
  onInvalidate?: () => void
}

export const AssetTypeFromAssigneeType = {
  [AssigneeType.Article]: AssetType.Article,
  [AssigneeType.Project]: AssetType.Project,
  [AssigneeType.MediaFile]: AssetType.MediaFile,
  [AssigneeType.Design]: AssetType.Design,
  [AssigneeType.Collection]: AssetType.Project,
  [AssigneeType.Video]: AssetType.Video,
}

export const AssigneeTypeFromAssetType = {
  [AssetType.Project]: AssigneeType.Project,
  [AssetType.Article]: AssigneeType.Article,
  [AssetType.Handbook]: AssigneeType.Project,
  [AssetType.MediaFile]: AssigneeType.MediaFile,
  [AssetType.Design]: AssigneeType.Design,
  [AssetType.Video]: AssigneeType.Video,
}

export const useAlmanacFileSubscription = ({ asset, onAttributesChange, onPropertyChange, onInvalidate }: Props) => {
  const websocketConnection = useWebsocketConnection()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const propertiesQuery = useProperties({}, {}, { pushToStore: true })

  useEffect(() => {
    if (!websocketConnection) return

    const subscription = websocketConnection.subscriptions.create(
      { channel: 'AlmanacFileChannel', asset_id: asset.id, asset_type: asset.type },
      {
        received: ({ op, session_uuid, entities, action }: InvalidatePayload) => {
          switch (op) {
            case Operation.Invalidate:
              if (entities.length < 1) {
                queryClient.invalidateQueries(almanacFileKeys.detail(asset.id))
                onInvalidate?.()
              }
              entities.forEach((entity) => {
                if (entity.type === EntityType.Properties) {
                  // If adding a property through the Finder
                  if (action === Action.addProperty) {
                    propertiesQuery.refetch()
                    return
                  }

                  const client = PropertyClient.getAssignedProperties<AssignedProperty>(
                    asset.id,
                    AssigneeTypeFromAssetType[asset.type],
                    entity.property_id,
                  )
                  // property_id is required, there will only ever be 1 page
                  client.loadMore().then((response) => {
                    if (response && response.type === SUCCESS_RESPONSE_TYPE) {
                      if (response.data.assigned_properties.length === 0 || action === Action.deleteProperty) {
                        // The property has been unassigned, so delete it from the file assigned properties
                        dispatch(
                          deleteFilesProperties(
                            [{ id: asset.id, type: AssigneeTypeFromAssetType[asset.type] }],
                            entity.property_id,
                          ),
                        )

                        const workspaceProperties = propertiesQuery.data?.data?.properties.filter(
                          (p: Property) => !p.scope_project_id,
                        )
                        const isDefaultWorkspaceProperty = workspaceProperties?.some(
                          (p: Property) => p.id === entity.property_id && !!p?.default_for_articles,
                        )
                        // when removing a default ws property from the folder, we actually hide it, not delete it
                        // therefore we don't want to remove it from the list of file properties
                        if (!isDefaultWorkspaceProperty) {
                          dispatch(removeProperty(entity.property_id))
                        }
                      }
                      dispatch(upsertFilesProperties(response.data.assigned_properties))
                      onPropertyChange?.(entity.property_id, response.data.assigned_properties)
                    }
                  })
                }
                if (entity.type === EntityType.Attributes) {
                  onAttributesChange?.({ session_uuid })
                }
                if (entity.type === EntityType.Article) {
                  queryClient.invalidateQueries([articlesKeys.prefix, 'detail', entity.id])
                }
                if (
                  entity.type === EntityType.InformalFeedbackRequests &&
                  [AssetType.Article, AssetType.Design].includes(asset.type)
                ) {
                  queryClient.invalidateQueries([informalFeedbackRequestsKeys.prefix, asset.id])
                  queryClient.invalidateQueries(informalFeedbackRequestsKeys.forAlmanacFile(asset.id))
                }
              })
              break
            default:
          }
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [
    asset.id,
    asset.type,
    dispatch,
    websocketConnection,
    queryClient,
    onAttributesChange,
    onPropertyChange,
    propertiesQuery,
    onInvalidate,
  ])
}
